import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import { ArrowLeft, Upload, Download, FileSpreadsheet, CheckCircle, XCircle, AlertCircle, RefreshCw, Info, Table } from "lucide-react";
import AppShell from "@/components/layout/AppShell";
import PageHeroCarousel from "@/components/common/PageHeroCarousel";
import { HERO_IMAGES } from "@/components/common/PageHero";
import FeatureGate from "@/components/FeatureGate";
import SignInBulkGrid from "@/components/children-records/SignInBulkGrid";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BULK_UPLOAD_CAROUSEL = [
  { url: HERO_IMAGES.bulkUpload, alt: "Bulk Upload dashboard" },
  { url: `${BACKEND_URL}/api/carousel/bulk_upload_1.png`, alt: "Data table with children records" },
  { url: `${BACKEND_URL}/api/carousel/bulk_upload_2.png`, alt: "Bulk Upload interface" },
  { url: `${BACKEND_URL}/api/carousel/bulk_upload_3.png`, alt: "Bulk Upload form" },
  { url: `${BACKEND_URL}/api/carousel/bulk_upload_4.png`, alt: "Workflow illustration" },
  { url: `${BACKEND_URL}/api/carousel/bulk_upload_5.png`, alt: "Team collaborating with data" },
  { url: `${BACKEND_URL}/api/carousel/bulk_upload_6.png`, alt: "Cloud data upload" },
  { url: `${BACKEND_URL}/api/carousel/bulk_upload_7.png`, alt: "Cloud upload dashboard" },
  { url: `${BACKEND_URL}/api/carousel/bulk_upload_8.png`, alt: "Data table in classroom" },
  { url: `${BACKEND_URL}/api/carousel/bulk_upload_9.png`, alt: "Data interface in classroom" },
  { url: "https://customer-assets.emergentagent.com/job_218b6b44-61cc-41e4-8731-b85bf1aa4557/artifacts/o72am582_image.png", alt: "Bulk upload in classroom" },
];

const BulkUploadPage = () => {
  const navigate = useNavigate();
  const { organization } = useAuth();
  const [activeTab, setActiveTab] = useState("bulk-upload");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [updateExisting, setUpdateExisting] = useState(false);
  const [reactivateDeleted, setReactivateDeleted] = useState(false);
  const [uploadGuideOpen, setUploadGuideOpen] = useState(true);

  // Bulk Upload grid state
  const [children, setChildren] = useState([]);
  const [loadingGrid, setLoadingGrid] = useState(false);

  // Health & Medical bulk upload (Sheet 2 — optional)
  const [healthFile, setHealthFile] = useState(null);
  const [healthUploading, setHealthUploading] = useState(false);
  const [healthResult, setHealthResult] = useState(null);

  const handleDownloadHealthTemplate = async () => {
    try {
      const response = await axios.get(`${API}/children/download-health-template`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'children_health_info_template.xlsx');
      document.body.appendChild(link); link.click(); link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success("Health & Medical template downloaded");
    } catch (error) {
      toast.error("Failed to download Health & Medical template");
    }
  };

  const handleHealthFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setHealthFile(e.target.files[0]);
      setHealthResult(null);
    }
  };

  const handleHealthUpload = async () => {
    if (!healthFile) {
      toast.error("Please select a file to upload");
      return;
    }
    setHealthUploading(true);
    const formData = new FormData();
    formData.append('file', healthFile);
    try {
      const response = await axios.post(`${API}/children/bulk-upload-health`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setHealthResult(response.data);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload Health & Medical file");
    } finally {
      setHealthUploading(false);
    }
  };

  const fetchChildren = async () => {
    setLoadingGrid(true);
    try {
      const res = await axios.get(`${API}/children`);
      const kids = (res.data || []).filter(c => c.active !== false);
      setChildren(kids);
    } catch (err) {
      console.error("Error fetching children:", err);
      toast.error("Failed to load children");
    } finally {
      setLoadingGrid(false);
    }
  };

  // Fetch grid data when Bulk Upload tab is first activated
  useEffect(() => {
    if (activeTab === "bulk-upload" && children.length === 0) {
      fetchChildren();
    }
  }, [activeTab]);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const uploadedFile = e.dataTransfer.files[0];
      if (uploadedFile.name.endsWith('.xlsx') || uploadedFile.name.endsWith('.xls') || uploadedFile.name.endsWith('.csv')) {
        setFile(uploadedFile);
        setResult(null);
      } else {
        toast.error("Please upload an Excel (.xlsx, .xls) or CSV file");
      }
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const uploadedFile = e.target.files[0];
      if (uploadedFile.name.endsWith('.xlsx') || uploadedFile.name.endsWith('.xls') || uploadedFile.name.endsWith('.csv')) {
        setFile(uploadedFile);
        setResult(null);
      } else {
        toast.error("Please upload an Excel (.xlsx, .xls) or CSV file");
      }
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      // Download template from API endpoint (includes Email columns)
      const response = await axios.get(`${API}/children/download-template`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'children_import_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success("Template downloaded successfully");
    } catch (error) {
      console.error("Error downloading template:", error);
      toast.error("Failed to download template");
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error("Please select a file to upload");
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(
        `${API}/children/bulk-upload?update_existing=${updateExisting}&reactivate_deleted=${reactivateDeleted}`, 
        formData, 
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setResult(response.data);
      
      // Show appropriate toast based on results
      const { created_count, skipped_count, reactivated_count, error_count } = response.data;
      
      if (created_count > 0 && error_count === 0) {
        toast.success(response.data.message);
      } else if (created_count === 0 && skipped_count > 0) {
        toast.info(`All ${skipped_count} children already exist - no new children added`);
      } else if (error_count > 0) {
        toast.warning(response.data.message);
      } else {
        toast.success(response.data.message);
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      toast.error(error.response?.data?.detail || "Failed to upload file");
    } finally {
      setUploading(false);
    }
  };

  const handleClear = () => {
    setFile(null);
    setResult(null);
    setReactivateDeleted(false);
  };

  return (
    <AppShell pageTitle="Child Enrollment Bulk Upload" breadcrumb>
      {/* Hero Carousel */}
      <PageHeroCarousel
        images={BULK_UPLOAD_CAROUSEL}
        title="Child Enrollment Bulk Upload"
        subtitle="Import multiple children at once from Excel or CSV"
        altText="Bulk upload"
      />
      
      <FeatureGate feature="bulk_upload">
      <div className="relative z-10">

        {/* Tab Pills */}
        <div className="flex gap-3 mb-6">
          <button
            onClick={() => setActiveTab("bulk-upload")}
            data-testid="bulk-upload-tab"
            className={`px-6 py-3 rounded-xl font-semibold text-base transition-all border-2 ${
              activeTab === "bulk-upload"
                ? "bg-teal-600 text-white border-teal-600 shadow-lg"
                : "bg-white text-teal-700 border-teal-400 hover:bg-teal-50 hover:border-teal-500 shadow-md"
            }`}
          >
            Child Enrollment Bulk Upload
          </button>
          <button
            onClick={() => setActiveTab("mass-template")}
            data-testid="mass-bulk-upload-template-tab"
            className={`px-6 py-3 rounded-xl font-semibold text-base transition-all border-2 ${
              activeTab === "mass-template"
                ? "bg-teal-600 text-white border-teal-600 shadow-lg"
                : "bg-white text-teal-700 border-teal-400 hover:bg-teal-50 hover:border-teal-500 shadow-md"
            }`}
          >
            Upload Template
          </button>
        </div>

        {/* ── Tab 1: Bulk Upload (In-App Grid) ── */}
        {activeTab === "bulk-upload" && (
          <div className="space-y-4">
            <div className="bg-teal-50 p-4 rounded-lg border border-teal-200">
              <div className="flex items-start gap-2">
                <Info className="w-5 h-5 text-teal-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-teal-700">
                  <p className="font-semibold mb-2">How to use Child Enrollment Bulk Upload:</p>
                  <ol className="list-decimal list-inside space-y-1 text-teal-600">
                    <li>All active children load automatically as columns. Click <strong>Refresh</strong> to pick up any newly enrolled children.</li>
                    <li>View each child's enrollment details — Name, Category, School, Parent/Guardian, Phone, Email.</li>
                    <li>This grid syncs <strong>Guardian / Pickup</strong> info only — not Emergency Contacts, Health, or Medical info.</li>
                    <li>Use <strong>Upload Template</strong> tab to import new children from Excel.</li>
                    <li>Archived children are automatically excluded from this grid.</li>
                  </ol>

                  {/* When to use this page — collapsible, open by default */}
                  <div className="mt-3 rounded-lg border border-teal-200 overflow-hidden">
                    <button
                      className="w-full flex items-center justify-between px-3 py-2 bg-teal-100 hover:bg-teal-200 transition-colors text-sm font-semibold text-teal-800"
                      onClick={() => setUploadGuideOpen(o => !o)}
                    >
                      <span className="flex items-center gap-2">
                        <Info className="w-4 h-4 text-teal-600" />
                        When to use this page
                      </span>
                      <span className="text-teal-500 text-base leading-none">{uploadGuideOpen ? '▲' : '▼'}</span>
                    </button>
                    {uploadGuideOpen && (
                      <div className="px-3 py-3 bg-white text-sm text-gray-700 space-y-2 border-t border-teal-100">
                        <p>
                          <span className="font-semibold text-teal-700">Starting fresh?</span>{' '}
                          Download the Template → fill in your children → upload here. All children will be created automatically.
                        </p>
                        <p>
                          <span className="font-semibold text-teal-700">From this point forward</span>{' '}
                          → edit directly in the app (Enrolled Children or the grid above) — that's always your most current information. No need to re-download or re-upload Excel for everyday updates.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg border p-4">
              <SignInBulkGrid
                children={children}
                onRefresh={fetchChildren}
                loading={loadingGrid}
              />
            </div>
          </div>
        )}

        {/* ── Tab 2: Bulk Upload Template ── */}
        {activeTab === "mass-template" && (
        <>
        {/* Instructions Card */}
        <Card className="bg-brand-primary/5 border border-brand-primary/20 mb-6 shadow-card">
          <CardHeader>
            <CardTitle className="text-xl text-brand-primary flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              How to Use - Excel Format Guide
            </CardTitle>
          </CardHeader>
          <CardContent className="text-ink-main space-y-4">
            <ol className="list-decimal list-inside space-y-2">
              <li><strong>Download the template</strong> - Click the green button below</li>
              <li><strong>Keep the header row (Row 1)</strong> - DO NOT delete or modify headers</li>
              <li><strong>Replace sample data</strong> - Delete rows 2-3 and add your children</li>
              <li><strong>Add children in any order</strong> - The app will automatically alphabetize them!</li>
              <li><strong>Save as .xlsx</strong> - Excel format</li>
              <li><strong>Upload</strong> - Drag or click to upload your file</li>
            </ol>

            {/* When to use this page — collapsible, open by default */}
            <div className="rounded-lg border border-brand-primary/20 overflow-hidden">
              <button
                className="w-full flex items-center justify-between px-4 py-3 bg-brand-primary/10 hover:bg-brand-primary/15 transition-colors text-sm font-semibold text-brand-primary"
                onClick={() => setUploadGuideOpen(o => !o)}
              >
                <span className="flex items-center gap-2">
                  <Info className="w-4 h-4" />
                  When to use this page
                </span>
                <span className="text-base leading-none">{uploadGuideOpen ? '▲' : '▼'}</span>
              </button>
              {uploadGuideOpen && (
                <div className="px-4 py-3 bg-white text-sm text-ink-main space-y-2 border-t border-brand-primary/10">
                  <p>
                    <span className="font-semibold text-brand-primary">Starting fresh?</span>{' '}
                    Download the Template → fill in your children → upload here. All children will be created automatically.
                  </p>
                  <p>
                    <span className="font-semibold text-brand-primary">From this point forward</span>{' '}
                    → edit directly in the app (Enrolled Children or the Bulk Upload grid) — that's always your most current information. This template is for initial setup only.
                  </p>
                </div>
              )}
            </div>
            
            {/* Auto-alphabetize notice */}
            <div className="p-3 bg-success/10 rounded-lg border border-success/30 flex items-center gap-2">
              <span className="text-2xl">🔤</span>
              <div>
                <p className="font-semibold text-success">Automatic Alphabetization!</p>
                <p className="text-ink-muted text-sm">
                  Teachers can add children in any order - even at the bottom of the list. 
                  The app automatically sorts all children alphabetically by name.
                </p>
              </div>
            </div>
            
            {/* Visual Example */}
            <div className="mt-4 p-4 bg-white rounded-lg border border-line">
              <p className="font-bold text-brand-primary mb-3 flex items-center gap-2">
                <Table className="w-5 h-5" /> Example Excel Format:
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-brand-primary/10">
                      <th className="border border-line px-2 py-1 text-center font-bold">Name</th>
                      <th className="border border-line px-2 py-1 text-center font-bold">Category</th>
                      <th className="border border-line px-2 py-1 text-center font-bold">School</th>
                      <th className="border border-line px-2 py-1 text-center font-bold">Parent/Guardian 1</th>
                      <th className="border border-line px-2 py-1 text-center font-bold">Contact Phone 1</th>
                      <th className="border border-line px-2 py-1 text-center font-bold bg-brand-accent/10">Email 1</th>
                      <th className="border border-line px-2 py-1 text-center font-bold">Special Instructions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="bg-brand-highlight/5">
                      <td className="border border-line px-2 py-1 text-center">Anderson, Noah</td>
                      <td className="border border-line px-2 py-1 text-center">Full Time</td>
                      <td className="border border-line px-2 py-1 text-center"></td>
                      <td className="border border-line px-2 py-1 text-center">Sarah Anderson</td>
                      <td className="border border-line px-2 py-1 text-center">(724) 555-1234</td>
                      <td className="border border-line px-2 py-1 text-center bg-brand-accent/5">sarah@email.com</td>
                      <td className="border border-line px-2 py-1 text-center">Peanut allergy</td>
                    </tr>
                    <tr>
                      <td className="border border-line px-2 py-1 text-center">Brown, Emma</td>
                      <td className="border border-line px-2 py-1 text-center">School Age</td>
                      <td className="border border-blue-300 px-2 py-1 text-center">Lincoln Elem</td>
                      <td className="border border-blue-300 px-2 py-1 text-center">Mike Brown</td>
                      <td className="border border-blue-300 px-2 py-1 text-center">(724) 555-5678</td>
                      <td className="border border-blue-300 px-2 py-1 text-center bg-purple-50">mike@email.com</td>
                      <td className="border border-blue-300 px-2 py-1 text-center"></td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-blue-600 mt-2">
                <strong>Template has 19 columns:</strong> Name, Category, School, Parent/Guardian 1-5, Contact Phone 1-5, <span className="text-purple-600 font-bold">Email 1-5</span>, Special Instructions
              </p>
            </div>

            {/* Column Requirements */}
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-3 bg-green-50 rounded-lg border border-green-300">
                <p className="font-bold text-green-800">✅ Required Columns:</p>
                <ul className="list-disc list-inside text-sm">
                  <li><strong>Name</strong> - Child's full name</li>
                  <li><strong>Parent/Guardian 1</strong> - Primary contact name</li>
                  <li><strong>Contact Phone 1</strong> - Format: (xxx) xxx-xxxx</li>
                </ul>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg border border-gray-300">
                <p className="font-bold text-gray-800">📝 Optional Columns:</p>
                <ul className="list-disc list-inside text-sm">
                  <li><strong>Category</strong> - Full Time, Part Time, School Age, After School</li>
                  <li><strong>School</strong> - For school age children</li>
                  <li><strong className="text-purple-700">Email 1-5</strong> - Parent email for messaging</li>
                  <li><strong>Special Instructions</strong> - Pickup notes or restrictions</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Download Template Button */}
        <div className="mb-6">
          <Button
            onClick={handleDownloadTemplate}
            className="bg-green-500 hover:bg-green-600 text-white font-semibold rounded-full shadow-lg"
            data-testid="download-template-btn"
          >
            <Download className="w-5 h-5 mr-2" />
            Download Template File (with Email columns)
          </Button>
        </div>

        {/* Upload Area */}
        <Card className="bg-white/80 backdrop-blur-sm border-2 border-green-200 mb-6">
          <CardHeader>
            <CardTitle className="text-2xl text-green-800">Upload File</CardTitle>
          </CardHeader>
          <CardContent>
            <div
              className={`border-2 border-dashed rounded-lg p-12 text-center transition-colors ${
                dragActive
                  ? 'border-green-500 bg-green-50'
                  : 'border-green-300 hover:border-green-400 hover:bg-green-50'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <FileSpreadsheet className="w-16 h-16 text-green-500 mx-auto mb-4" />
              
              {file ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-center gap-2 text-green-800">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <p className="font-semibold">{file.name}</p>
                  </div>
                  <p className="text-sm text-green-600">
                    {(file.size / 1024).toFixed(2)} KB
                  </p>
                  
                  {/* Update Existing Option */}
                  <div className="flex items-center justify-center gap-2 p-3 bg-green-50 rounded-lg border border-green-200 max-w-md mx-auto">
                    <Checkbox
                      id="update-existing"
                      checked={updateExisting}
                      onCheckedChange={setUpdateExisting}
                      className="border-green-400"
                    />
                    <Label htmlFor="update-existing" className="text-green-800 cursor-pointer">
                      <RefreshCw className="w-4 h-4 inline mr-1" />
                      Update pickup persons for existing children
                    </Label>
                  </div>
                  {updateExisting && (
                    <p className="text-xs text-green-600 max-w-md mx-auto">
                      Existing children's pickup persons will be replaced with the data from the file. Sign-in history and notes will be preserved.
                    </p>
                  )}
                  
                  {/* Reactivate deleted children option */}
                  <div className="flex items-center justify-center gap-2 p-3 bg-purple-50 rounded-lg border border-purple-200 max-w-md mx-auto">
                    <Checkbox
                      id="reactivate-deleted"
                      checked={reactivateDeleted}
                      onCheckedChange={setReactivateDeleted}
                      className="border-purple-400"
                    />
                    <Label htmlFor="reactivate-deleted" className="text-purple-800 cursor-pointer">
                      Reactivate previously deleted/deactivated children
                    </Label>
                  </div>
                  {reactivateDeleted && (
                    <p className="text-xs text-purple-600 max-w-md mx-auto">
                      Children you previously deactivated will be restored if they appear in this file.
                    </p>
                  )}
                  
                  <div className="flex gap-3 justify-center">
                    <Button
                      onClick={handleUpload}
                      disabled={uploading}
                      className="bg-green-600 hover:bg-green-700 text-white rounded-full"
                      data-testid="upload-btn"
                    >
                      {uploading ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Upload className="w-5 h-5 mr-2" />
                          Upload & Import
                        </>
                      )}
                    </Button>
                    <Button
                      onClick={handleClear}
                      variant="outline"
                      className="border-green-300 text-green-700 rounded-full"
                    >
                      Clear
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-lg text-green-800 font-semibold">
                    Drag and drop your file here
                  </p>
                  <p className="text-green-600">or</p>
                  <div>
                    <input
                      id="file-upload"
                      type="file"
                      accept=".xlsx,.xls,.csv"
                      onChange={handleFileChange}
                      className="hidden"
                      data-testid="file-input"
                    />
                    <label 
                      htmlFor="file-upload"
                      className="inline-flex items-center justify-center px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-full cursor-pointer font-semibold transition-colors"
                    >
                      <Upload className="w-5 h-5 mr-2" />
                      Choose File
                    </label>
                  </div>
                  <p className="text-sm text-green-600">
                    Supports Excel (.xlsx, .xls) and CSV files
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Health & Medical Info — optional second sheet */}
        <Card className="bg-white/80 backdrop-blur-sm border-2 border-blue-200 mb-6" data-testid="health-sheet-card">
          <CardHeader>
            <CardTitle className="text-2xl text-blue-800 flex items-center gap-2">
              <FileSpreadsheet className="w-6 h-6" />
              Health & Medical Info (Optional Second Sheet)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 text-sm text-blue-800">
              <p className="font-semibold mb-1">This separate sheet covers:</p>
              <p>Health Insurance, Allergies, Disabilities, Medical Emergency Info, Medications, Special Needs, Physician, and Emergency Contacts.</p>
              <p className="mt-2 text-blue-700">
                You can fill this out now or later — completely optional. Update these manually in Edit Child, or download/edit/re-upload this sheet to update multiple children at once.
              </p>
            </div>

            <Button
              onClick={handleDownloadHealthTemplate}
              className="bg-blue-500 hover:bg-blue-600 text-white font-semibold rounded-full shadow-lg"
              data-testid="download-health-template-btn"
            >
              <Download className="w-5 h-5 mr-2" />
              Download Health & Medical Template
            </Button>

            <div className="border-2 border-dashed border-blue-300 rounded-lg p-6 text-center hover:border-blue-400 hover:bg-blue-50 transition-colors">
              {healthFile ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-center gap-2 text-blue-800">
                    <CheckCircle className="w-5 h-5 text-blue-600" />
                    <p className="font-semibold">{healthFile.name}</p>
                  </div>
                  <div className="flex gap-3 justify-center">
                    <Button
                      onClick={handleHealthUpload}
                      disabled={healthUploading}
                      className="bg-blue-600 hover:bg-blue-700 text-white rounded-full"
                      data-testid="upload-health-btn"
                    >
                      {healthUploading ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Upload className="w-5 h-5 mr-2" />
                          Upload Health Sheet
                        </>
                      )}
                    </Button>
                    <Button
                      onClick={() => { setHealthFile(null); setHealthResult(null); }}
                      variant="outline"
                      className="border-blue-300 text-blue-700 rounded-full"
                      data-testid="clear-health-file-btn"
                    >
                      Clear
                    </Button>
                  </div>
                </div>
              ) : (
                <div>
                  <input
                    id="health-file-upload"
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    onChange={handleHealthFileChange}
                    className="hidden"
                    data-testid="health-file-input"
                  />
                  <label
                    htmlFor="health-file-upload"
                    className="inline-flex items-center justify-center px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-full cursor-pointer font-semibold transition-colors"
                  >
                    <Upload className="w-5 h-5 mr-2" />
                    Choose Health Sheet File
                  </label>
                  <p className="text-sm text-blue-600 mt-2">Names must match your roster exactly (upload Sheet 1 first)</p>
                </div>
              )}
            </div>

            {healthResult && (
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 text-sm" data-testid="health-upload-result">
                <p className="font-semibold text-blue-800">{healthResult.message}</p>
                {healthResult.not_found_names?.length > 0 && (
                  <p className="text-blue-700 mt-1">Not found: {healthResult.not_found_names.join(', ')}</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Results */}
        {result && (
          <Card className="bg-white/80 backdrop-blur-sm border-2 border-amber-200">
            <CardHeader>
              <CardTitle className="text-2xl text-amber-800">Import Results</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Summary */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-center gap-2 text-green-800 mb-2">
                    <CheckCircle className="w-5 h-5" />
                    <p className="font-semibold text-sm">New</p>
                  </div>
                  <p className="text-3xl font-bold text-green-800">{result.created_count || 0}</p>
                </div>
                
                {(result.updated_count || 0) > 0 && (
                  <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                    <div className="flex items-center gap-2 text-amber-800 mb-2">
                      <RefreshCw className="w-5 h-5" />
                      <p className="font-semibold text-sm">Updated</p>
                    </div>
                    <p className="text-3xl font-bold text-amber-800">{result.updated_count}</p>
                  </div>
                )}
                
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-center gap-2 text-blue-800 mb-2">
                    <AlertCircle className="w-5 h-5" />
                    <p className="font-semibold text-sm">Skipped</p>
                  </div>
                  <p className="text-3xl font-bold text-blue-800">{result.skipped_count || 0}</p>
                </div>
                
                {(result.reactivated_count || 0) > 0 && (
                  <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                    <div className="flex items-center gap-2 text-purple-800 mb-2">
                      <CheckCircle className="w-5 h-5" />
                      <p className="font-semibold text-sm">Reactivated</p>
                    </div>
                    <p className="text-3xl font-bold text-purple-800">{result.reactivated_count}</p>
                  </div>
                )}
                
                {(result.error_count || 0) > 0 && (
                  <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                    <div className="flex items-center gap-2 text-red-800 mb-2">
                      <XCircle className="w-5 h-5" />
                      <p className="font-semibold text-sm">Errors</p>
                    </div>
                    <p className="text-3xl font-bold text-red-800">{result.error_count}</p>
                  </div>
                )}
                
                {(result.mismatch_count || 0) > 0 && (
                  <div className="p-4 bg-yellow-50 rounded-lg border-2 border-yellow-400">
                    <div className="flex items-center gap-2 text-yellow-800 mb-2">
                      <AlertCircle className="w-5 h-5" />
                      <p className="font-semibold text-sm">Mismatches</p>
                    </div>
                    <p className="text-3xl font-bold text-yellow-800">{result.mismatch_count}</p>
                  </div>
                )}
              </div>
              
              {/* Info about updated children */}
              {(result.updated_count || 0) > 0 && (
                <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <p className="text-amber-800 text-sm">
                    🔄 <strong>{result.updated_count} children had their pickup persons updated</strong> - sign-in history and notes remain intact.
                  </p>
                </div>
              )}
              
              {/* Info about existing children */}
              {(result.skipped_count || 0) > 0 && (
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-blue-800 text-sm">
                    ℹ️ <strong>{result.skipped_count} existing children were preserved</strong> - their records, notes, and sign-in history remain unchanged.
                  </p>
                </div>
              )}

              {/* Mismatch Warning */}
              {result.details && result.details.some(d => d.includes('MISMATCH')) && (
                <div className="p-4 bg-yellow-50 rounded-lg border-2 border-yellow-400">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-6 h-6 text-yellow-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-bold text-yellow-800 mb-1">⚠️ Data Mismatches Detected - Manual Input Preserved!</p>
                      <p className="text-yellow-700 text-sm mb-2">
                        Some children have different information in your Excel template compared to what's saved in the app. 
                        <strong> The app data (manual input) has been preserved and NOT overwritten.</strong>
                      </p>
                      <p className="text-yellow-800 text-sm font-semibold">
                        Action Required: Update your Excel template to match the correct information in the app.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Debug Info - File Parsing Details */}
              {result.debug_info && (
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-300">
                  <h3 className="font-semibold text-gray-800 mb-2">📊 File Debug Info:</h3>
                  <div className="text-sm text-gray-600 space-y-1">
                    <p><strong>File:</strong> {result.debug_info.file_name}</p>
                    <p><strong>Rows read from file:</strong> {result.debug_info.rows_read}</p>
                    <p><strong>Rows after filtering:</strong> {result.debug_info.rows_after_filter}</p>
                    {result.debug_info.plan && (
                      <p><strong>Your plan:</strong> {result.debug_info.plan} (limit: {result.debug_info.plan_limit} children)</p>
                    )}
                    {result.debug_info.current_active_children !== undefined && (
                      <p><strong>Current active children:</strong> {result.debug_info.current_active_children} | <strong>Slots available:</strong> {result.debug_info.slots_available}</p>
                    )}
                    {result.debug_info.columns_found && (
                      <p><strong>Columns detected:</strong> {result.debug_info.columns_found.join(', ')}</p>
                    )}
                    {result.debug_info.instruction_row_detected && (
                      <p><strong>Instruction row:</strong> {result.debug_info.instruction_row_detected}</p>
                    )}
                    {result.debug_info.last_5_names && result.debug_info.last_5_names.length > 0 && (
                      <div>
                        <p><strong>Last {result.debug_info.last_5_names.length} names found in file:</strong></p>
                        <ul className="list-disc list-inside ml-4 mt-1">
                          {result.debug_info.last_5_names.map((name, idx) => (
                            <li key={idx}>{name || '(empty)'}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Details List */}
              {result.details && result.details.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-amber-800">Details:</h3>
                    <Button
                      onClick={() => setResult({...result, details: []})}
                      variant="outline"
                      size="sm"
                      className="border-gray-300 text-gray-600 hover:bg-gray-100"
                    >
                      <XCircle className="w-4 h-4 mr-1" />
                      Clear Messages
                    </Button>
                  </div>
                  <div className="max-h-60 overflow-y-auto space-y-2">
                    {result.details.map((detail, index) => (
                      <div
                        key={index}
                        className={`p-3 border rounded text-sm ${
                          detail.includes('MISMATCH')
                            ? 'bg-yellow-100 border-yellow-400 text-yellow-900 font-semibold'
                            : detail.includes('AGE GROUP ERROR')
                            ? 'bg-red-100 border-red-300 text-red-800 font-semibold'
                            : detail.includes('WARNING: No age group')
                            ? 'bg-orange-100 border-orange-300 text-orange-800'
                            : detail.includes('SKIPPED') 
                            ? 'bg-blue-50 border-blue-200 text-blue-700'
                            : detail.includes('REACTIVATED')
                            ? 'bg-purple-50 border-purple-200 text-purple-700'
                            : detail.includes('CREATED')
                            ? 'bg-green-50 border-green-200 text-green-700'
                            : detail.includes('UPDATED')
                            ? 'bg-amber-50 border-amber-200 text-amber-700'
                            : detail.includes('VERIFIED')
                            ? 'bg-teal-50 border-teal-200 text-teal-700'
                            : 'bg-red-50 border-red-200 text-red-700'
                        }`}
                      >
                        {detail}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3">
                <Button
                  onClick={() => navigate('/children')}
                  className="bg-amber-500 hover:bg-amber-600 text-white rounded-full"
                >
                  View Children List
                </Button>
                <Button
                  onClick={handleClear}
                  variant="outline"
                  className="border-brand-primary text-brand-primary rounded-xl"
                >
                  Upload Another File
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
        </>
        )}
      </div>
      </FeatureGate>
    </AppShell>
  );
};

export default BulkUploadPage;
